package com.ijoin.ui.components

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Close
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import coil3.compose.AsyncImage
import com.ijoin.data.api.ApiClient
import com.ijoin.i18n.strings
import com.ijoin.platform.MobileOS
import com.ijoin.platform.openUrl
import com.ijoin.ui.design.EventCardAccentDark
import com.ijoin.ui.design.EventCardAccentSoft
import com.ijoin.ui.design.EventCardCorner
import com.ijoin.ui.design.EventCardCompactCorner
import com.ijoin.ui.design.EventCardOutline
import com.ijoin.ui.design.EventCardSurface
import com.ijoin.ui.design.EventDialog
import com.ijoin.ui.design.EventSurfaceCard
import com.ijoin.ui.design.EventTextAction

// Badge image paths (using AsyncImage instead of painterResource to avoid Skia decoder issues on Web)
private const val BADGE_APP_STORE_PATH = "/api/media/common/badge_app_store.png"
private const val BADGE_GOOGLE_PLAY_PATH = "/api/media/common/badge_google_play.png"

private fun getBadgeAppStoreUrl() = "${ApiClient.getBaseUrl()}$BADGE_APP_STORE_PATH"
private fun getBadgeGooglePlayUrl() = "${ApiClient.getBaseUrl()}$BADGE_GOOGLE_PLAY_PATH"

/**
 * Banner shown to mobile web users encouraging them to download the native app.
 * Displays a styled App Store or Play Store button depending on the user's OS.
 */
@Composable
fun AppStoreBanner(
    mobileOS: MobileOS,
    onDismiss: () -> Unit,
    modifier: Modifier = Modifier
) {
    val strings = strings()
    var showGooglePlayUnavailableDialog by remember { mutableStateOf(false) }

    val gradient = Brush.linearGradient(
        colors = listOf(
            EventCardAccentSoft,
            EventCardSurface
        )
    )

    EventSurfaceCard(
        modifier = modifier.fillMaxWidth(),
        backgroundColor = EventCardSurface,
        borderColor = EventCardOutline,
        contentPadding = PaddingValues(0.dp)
    ) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .background(gradient, RoundedCornerShape(EventCardCorner))
                .padding(horizontal = 18.dp, vertical = 14.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(16.dp)
        ) {
            Column(
                modifier = Modifier.weight(1f),
                verticalArrangement = Arrangement.spacedBy(8.dp)
            ) {
                Text(
                    text = strings.appStoreBannerTitle,
                    style = MaterialTheme.typography.titleMedium,
                    fontWeight = FontWeight.SemiBold,
                    color = EventCardAccentDark
                )
                Text(
                    text = strings.appStoreBannerMessage,
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant
                )
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.Center
                ) {
                    when (mobileOS) {
                        MobileOS.IOS -> StoreBadge(
                            imageUrl = getBadgeAppStoreUrl(),
                            contentDescription = strings.downloadOnAppStore,
                            onClick = { openUrl("https://apps.apple.com/us/app/ijoinapp/id6758534708") }
                        )
                        MobileOS.ANDROID -> StoreBadge(
                            imageUrl = getBadgeGooglePlayUrl(),
                            contentDescription = strings.getItOnGooglePlay,
                            onClick = { showGooglePlayUnavailableDialog = true }
                        )
                        MobileOS.NONE -> Unit
                    }
                }
            }

            IconButton(
                onClick = onDismiss,
                modifier = Modifier.size(34.dp)
            ) {
                Icon(
                    imageVector = Icons.Default.Close,
                    contentDescription = strings.close,
                    tint = EventCardAccentDark
                )
            }
        }
    }

    if (showGooglePlayUnavailableDialog) {
        StoreUnavailableDialog(onDismiss = { showGooglePlayUnavailableDialog = false })
    }
}

@Composable
private fun StoreBadge(
    imageUrl: String,
    contentDescription: String,
    onClick: () -> Unit,
    modifier: Modifier = Modifier
) {
    EventSurfaceCard(
        modifier = modifier
            .height(44.dp)
            .clickable { onClick() },
        cornerRadius = EventCardCompactCorner,
        backgroundColor = EventCardSurface,
        borderColor = EventCardOutline,
        shadowElevation = 4.dp,
        contentPadding = PaddingValues(horizontal = 12.dp, vertical = 6.dp)
    ) {
        AsyncImage(
            model = imageUrl,
            contentDescription = contentDescription,
            modifier = Modifier.height(32.dp),
            contentScale = ContentScale.Fit
        )
    }
}

/**
 * App store icons to show in the side navigation rail for desktop web users.
 * Shows both App Store and Play Store links stacked vertically.
 * Uses AsyncImage with URLs instead of painterResource to avoid Skia decoder issues on Web.
 */
@Composable
fun AppStoreLinks(
    modifier: Modifier = Modifier
) {
    val strings = strings()
    var showGooglePlayUnavailableDialog by remember { mutableStateOf(false) }

    Column(
        modifier = modifier.padding(8.dp),
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.spacedBy(8.dp)
    ) {
        // App Store badge
        AsyncImage(
            model = getBadgeAppStoreUrl(),
            contentDescription = strings.downloadOnAppStore,
            modifier = Modifier
                .width(120.dp)
                .clickable { openUrl("https://apps.apple.com/us/app/ijoinapp/id6758534708") },
            contentScale = ContentScale.Fit
        )

        // Google Play badge
        AsyncImage(
            model = getBadgeGooglePlayUrl(),
            contentDescription = strings.getItOnGooglePlay,
            modifier = Modifier
                .width(120.dp)
                .clickable { showGooglePlayUnavailableDialog = true },
            contentScale = ContentScale.Fit
        )
    }

    if (showGooglePlayUnavailableDialog) {
        StoreUnavailableDialog(onDismiss = { showGooglePlayUnavailableDialog = false })
    }
}

@Composable
private fun StoreUnavailableDialog(
    onDismiss: () -> Unit
) {
    val strings = strings()

    EventDialog(onDismissRequest = onDismiss) {
        Text(
            text = strings.appStoreUnavailableMessage,
            style = MaterialTheme.typography.bodyMedium,
            color = MaterialTheme.colorScheme.onSurfaceVariant
        )
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.End
        ) {
            EventTextAction(
                text = strings.ok,
                onClick = onDismiss
            )
        }
    }
}
